import styled from "@emotion/styled";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { lazy } from "react";

import { PageSection } from "@skilitics/designer-ui-components";
import { useFeatureSwitch } from "@skilitics/nautilus-core-designer";

import { useNautilusApi } from "../../../api/useNautilusApi";
import OpenAiLogo from "../../../assets/logos/open-ai.svg?react";
import { OpenAIForm } from "./integrations/OpenAIForm.tsx";

// Only for super-users, so skip loading all the logos.
const FakeIntegrationsPopup = lazy(async () => {
  const module = await import("./integrations/FakeIntegrationsPopup.tsx");
  return { default: module.FakeIntegrationsPopup };
});

const StyledPageSection = styled(PageSection)`
  margin-top: 20px;
`;

export const IntegrationsPage = () => {
  const isSuperUser = useFeatureSwitch("super-user");
  // We are not inside a NautilusApiContextProvider here, so
  // we can't use useCopilotApi(), which uses useNautilusContextApi()
  // (note the Context in the name).
  const api = useNautilusApi().copilot;

  const queryClient = useQueryClient();
  const current = useQuery({
    queryKey: ["settings", "integrations", "openai"],
    queryFn: async () => {
      return await api.getOpenAIApiKey();
    },
  });

  const handleSetApiKey = async (apiKey: string | null) => {
    await api.setOpenAIApiKey(apiKey);
    await queryClient.invalidateQueries({ queryKey: ["copilot"] });
    const result = await current.refetch();
    return result.data?.maskedValue ?? null;
  };

  return (
    <>
      <StyledPageSection header={<OpenAIHeader />}>
        <OpenAIForm
          maskedApiKey={current.data?.maskedValue ?? null}
          apiKeyState={current.data?.state}
          setApiKey={handleSetApiKey}
        />
      </StyledPageSection>
      {isSuperUser && <FakeIntegrationsPopup />}
    </>
  );
};

const OpenAIHeader = () => (
  <div>
    <OpenAiLogo />
    <p>Connect our Interact AI functionality through your own OpenAI account.</p>
  </div>
);
